/*
 * Copyright (c) 2024, King Living
 * SEO helpers (canonical, hreflang, shared gating).
 */

import {
  PASSWORDLESS_LOGIN_LANDING_PATH,
  RESET_PASSWORD_LANDING_PATH,
} from '@salesforce/retail-react-app/app/constants';
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url';
import {getParamsFromPath} from '@salesforce/retail-react-app/app/utils/site-utils';

import {DEFAULT_SITE_TITLE} from '@amblique/king-living/app/constants';
import {PAGE_BLOG_METADATA_TAG_IDS} from '@amblique/king-living/app/contentful/utils/blog-metadata-tag-ids';
import {
  richTextFirstBlockPlainText,
  richTextToPlainText,
} from '@amblique/king-living/app/contentful/utils/rich-text';
import {PERPETUAL_ATS_THRESHOLD} from '@amblique/king-living/app/utils/cover-stock-display';
import {resolveCommittedSelectionHeroVisualUrl} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {isMtoOrderableProduct} from '@amblique/king-living/app/utils/inventory-utils';
import {isSimpleProduct} from '@amblique/king-living/app/utils/product-utils';
import {resolveAbsoluteImageUrl} from '@amblique/king-living/app/utils/responsive-image-src';

const COUNTRY_TO_HREFLANG = {
  AU: 'en-au',
  NZ: 'en-nz',
  SG: 'en-sg',
  MY: 'en-my',
  CA: 'en-ca',
  UK: 'en-gb',
  US: 'en-us',
};

const UTILITY_ROUTE_PREFIXES = ['/account', '/checkout'];

const BLOG_ARTICLE_PAGE_TYPES = new Set(['blog', 'blog_article_img', 'blog_article_content']);

export const PRODUCT_SEO_EXPAND = ['availability', 'images', 'prices', 'variations'];

const EXACT_UTILITY_PATHS = new Set(
  [
    '/login',
    '/registration',
    '/reset-password',
    '/cart',
    '/cart/upsell',
    PASSWORDLESS_LOGIN_LANDING_PATH,
    RESET_PASSWORD_LANDING_PATH
  ].map((path) => normalizePathname(path).toLowerCase())
);

function normalizePathname(pathname) {
  if (!pathname || typeof pathname !== 'string') return '/';

  const withoutQuery = pathname.split('?')[0].split('#')[0];
  const path = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;

  return path.replace(/\/+$/, '') || '/';
}

function joinOriginPath(origin, pathname) {
  const base = origin.replace(/\/+$/, '');
  const path = normalizePathname(pathname);

  if (path === '/') return `${base}/`;

  return `${base}${path}`;
}

function getCrossRegionPathname(pathname) {
  const stripped = removeSiteLocaleFromPath(pathname);

  if (stripped === '' || stripped == null) {
    return '/';
  }

  return normalizePathname(stripped);
}

function buildCurrentPageUrl(appOrigin, pathname) {
  if (!appOrigin) return null;

  return joinOriginPath(appOrigin, pathname);
}

function buildRegionalAlternateUrl({hreflang, origin, pathname}) {
  const barePath = getCrossRegionPathname(pathname);

  if (hreflang === 'fr-ca') {
    const base = origin.replace(/\/+$/, '');

    if (barePath === '/fr' || barePath.startsWith('/fr/')) {
      return joinOriginPath(base, barePath);
    }

    if (barePath === '/') {
      return `${base}/fr/`;
    }

    return `${base}/fr${barePath}`;
  }

  return joinOriginPath(origin, barePath);
}

function isSeoUtilityRoute(pathname) {
  const bare = getCrossRegionPathname(pathname).toLowerCase();

  if (EXACT_UTILITY_PATHS.has(bare)) return true;

  return UTILITY_ROUTE_PREFIXES.some((prefix) => bare.startsWith(prefix));
}

function shouldEmitSeoLinks({noIndex, pathname}) {
  return !noIndex && !isSeoUtilityRoute(pathname);
}

function shouldEmitJsonLd({noIndex, pathname}) {
  return !noIndex && !isSeoUtilityRoute(pathname);
}

function resolveJsonLdPageUrl({appOrigin, pathname}) {
  if (!appOrigin) return null;

  return joinOriginPath(appOrigin, pathname);
}

function isBlogPageByType(page) {
  return BLOG_ARTICLE_PAGE_TYPES.has(String(page?.pageType || '').toLowerCase());
}

/**
 * Blog entry id from slim page mainRegion (direct Blog block only).
 * @param {object} [page]
 * @returns {string|null}
 */
export function findBlogIdInMainRegion(page) {
  for (const item of page?.mainRegionCollection?.items || []) {
    if (item?.__typename === 'Blog' && item?.sys?.id) {
      return item.sys.id;
    }
  }

  return null;
}

/**
 * Blog article pages — metadata tag `page:Blog` (TSD) with pageType fallback.
 * @param {object} [page]
 * @returns {boolean}
 */
export function isBlogArticlePage(page) {
  if (!page) return false;

  const tagIds = page.contentfulMetadata?.tags?.map((tag) => tag?.id).filter(Boolean) ?? [];

  if (PAGE_BLOG_METADATA_TAG_IDS.some((id) => tagIds.includes(id))) {
    return true;
  }

  return isBlogPageByType(page);
}

/** @param {object} [page] @returns {boolean} */
export function isFaqCmsPage(page) {
  return String(page?.pageType || '').toLowerCase() === 'faq';
}

/**
 * @param {{ regionalSeoLinks?: boolean, noIndex?: boolean, appOrigin?: string, pathname?: string, countries?: object|string, countryCode?: string }} args
 * @returns {{ canonicalUrl: string, alternates: { hreflang: string, href: string }[] }|null}
 */
export function resolveRegionalSeoLinks({regionalSeoLinks, noIndex, appOrigin, pathname, countries, countryCode}) {
  if (!regionalSeoLinks || !shouldEmitSeoLinks({noIndex, pathname})) return null;

  let prefs = countries;

  if (typeof prefs === 'string') {
    try {
      prefs = JSON.parse(prefs);
    } catch {
      prefs = null;
    }
  }

  const canonicalUrl = resolveCanonicalUrl({
    appOrigin,
    countries,
    countryCode,
    noIndex,
    pathname,
  });

  if (!canonicalUrl) return null;

  const hreflangAlternates = [];

  if (prefs && typeof prefs === 'object') {
    for (const key of Object.keys(COUNTRY_TO_HREFLANG)) {
      const origin = prefs[key]?.defaultSiteURL?.trim();

      if (!origin) continue;

      hreflangAlternates.push({hreflang: COUNTRY_TO_HREFLANG[key], origin});

      if (key === 'CA') {
        hreflangAlternates.push({hreflang: 'fr-ca', origin});
      }
    }
  }

  return {
    alternates: hreflangAlternates.map(({hreflang, origin}) => ({
      href: buildRegionalAlternateUrl({hreflang, origin, pathname}),
      hreflang,
    })),
    canonicalUrl,
  };
}

/**
 * @param {{ appOrigin?: string, pathname?: string, noIndex?: boolean, countries?: object|string, countryCode?: string }} args
 * @returns {string|null}
 */
export function resolveCanonicalUrl({appOrigin, pathname, noIndex, countries, countryCode}) {
  if (!shouldEmitSeoLinks({noIndex, pathname})) return null;

  let prefs = countries;

  if (typeof prefs === 'string') {
    try {
      prefs = JSON.parse(prefs);
    } catch {
      prefs = null;
    }
  }

  const siteOrigin = countryCode && prefs?.[countryCode]?.defaultSiteURL?.trim();

  if (!siteOrigin) {
    return buildCurrentPageUrl(appOrigin, pathname);
  }

  const {localeRef} = getParamsFromPath(pathname || '');
  const hreflang =
    countryCode === 'CA' && (localeRef === 'fr' || localeRef === 'fr-CA') ?
      'fr-ca' :
      COUNTRY_TO_HREFLANG[countryCode];

  return hreflang ?
    buildRegionalAlternateUrl({hreflang, origin: siteOrigin, pathname}) :
    joinOriginPath(siteOrigin, getCrossRegionPathname(pathname));
}

/**
 * @param {string} slug
 * @returns {string[]}
 */
export function buildSlugQueryVariants(slug) {
  if (!slug || typeof slug !== 'string') return [];

  const trimmed = slug.trim();

  if (!trimmed) return [];

  if (trimmed.startsWith('/')) {
    const withoutLeading = trimmed.slice(1);

    return withoutLeading && withoutLeading !== trimmed ?
      [trimmed, withoutLeading] :
      [trimmed];
  }

  return [`/${trimmed}`, trimmed];
}

/**
 * Normalise SEO description to plain text (Contentful Long text or Rich Text document).
 * @param {unknown} value
 * @returns {string|null}
 */
export function resolveSeoDescriptionText(value) {
  if (value == null) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();

    return trimmed || null;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    const trimmed = String(value).trim();

    return trimmed || null;
  }

  if (typeof value === 'object') {
    if (value.json) {
      return richTextToPlainText(value.json) || null;
    }

    if (value.nodeType === 'document') {
      return richTextToPlainText(value) || null;
    }

    if (Array.isArray(value.content)) {
      return richTextToPlainText({content: value.content, nodeType: 'document'}) || null;
    }
  }

  return null;
}

/**
 * @param {...({ title?: string, description?: string, keywords?: string, ogImage?: string }|null|undefined)} layers
 * @returns {{ title?: string, description?: string, keywords?: string, ogImage?: string }}
 */
export function mergeSeoSources(...layers) {
  return layers.reduce((result, layer) => {
    if (!layer) return result;

    for (const [key, value] of Object.entries(layer)) {
      const resolved = key === 'description' ? resolveSeoDescriptionText(value) : value;

      if (resolved != null && String(resolved).trim() !== '') {
        result[key] = typeof resolved === 'string' ? resolved.trim() : resolved;
      }
    }

    return result;
  }, {});
}

/**
 * @param {{ noIndex?: boolean, pathname?: string }} args
 * @returns {'INDEX,FOLLOW'|'NOINDEX,FOLLOW'}
 */
export function resolveRobotsContent({noIndex, pathname}) {
  if (noIndex || isSeoUtilityRoute(pathname)) return 'NOINDEX,FOLLOW';

  return 'INDEX,FOLLOW';
}

/**
 * @param {string} [pathname]
 * @returns {boolean}
 */
export function isHomePagePathname(pathname) {
  const bare = getCrossRegionPathname(pathname).toLowerCase();

  return bare === '/' || bare === '/home';
}

/**
 * @param {{ title?: string, isHomepage?: boolean, hasContentfulSeoTitle?: boolean, siteTitle?: string }} args
 * @returns {string}
 */
export function resolveTitleForDocument({
  title,
  isHomepage = false,
  hasContentfulSeoTitle = false,
  siteTitle = 'King Living',
}) {
  if (!title) return siteTitle;

  if (hasContentfulSeoTitle) return title;

  if (isHomepage) {
    const prefix = `${siteTitle} | `;

    return title.startsWith(prefix) ? title : `${prefix}${title}`;
  }

  return title;
}

/**
 * @param {{ page?: object, isProductPage?: boolean }} args
 * @returns {'website'|'product'|'article'}
 */
export function resolveOgType({page, isProductPage}) {
  if (isProductPage || page?.productId) return 'product';

  if (isBlogArticlePage(page)) return 'article';

  return 'website';
}

/**
 * PDP page-load hero image for schema / SCAPI og:image fallback.
 * Reuses {@link resolveCommittedSelectionHeroVisualUrl} (PIM → Cylindo → SFCC).
 * SEO still-image: omit dedicated `c_video` so resolver does not treat video as the hero.
 *
 * @param {object} [product]
 * @returns {string|null}
 */
export function resolveProductImageUrl(product) {
  if (!product) return null;

  const dedicatedVideo = product.c_video ?? product.video;
  const heroProduct =
    dedicatedVideo != null && String(dedicatedVideo).trim() !== '' ?
      {...product, c_video: undefined, video: undefined} :
      product;

  const heroResult = resolveCommittedSelectionHeroVisualUrl(heroProduct, {}, {
    allowCylindo: !isSimpleProduct(product),
  });

  let rawUrl = heroResult.url;
  const pimImages = heroResult.galleryResource?.pimImages;

  if (pimImages?.[0]?.mediaType === 'video') {
    const stillImage = pimImages.find(
      (img) => img.mediaType !== 'video' && (img.disBaseLink || img.link)
    );

    rawUrl = stillImage?.disBaseLink || stillImage?.link || null;
  }

  if (!rawUrl) return null;

  return resolveAbsoluteImageUrl(rawUrl) || null;
}

/**
 * @param {object} [product]
 * @returns {{ title?: string, description?: string, ogImage?: string }|null}
 */
export function resolveScapiSeoFromProduct(product) {
  if (!product) return null;

  return {
    description: product.pageDescription || product.shortDescription || null,
    ogImage: resolveProductImageUrl(product),
    title: product.pageTitle || product.name || null,
  };
}

/**
 * @param {object} [category]
 * @returns {{ title?: string, description?: string, keywords?: string }|null}
 */
export function resolveScapiSeoFromCategory(category) {
  if (!category) return null;

  return {
    description: category.pageDescription || null,
    keywords: category.pageKeywords || null,
    title: category.pageTitle || category.name || null,
  };
}

/**
 * @param {object} [page]
 * @returns {{ title?: string, description?: string, keywords?: string, ogImage?: string }|null}
 */
export function resolveContentfulSeoFromPage(page) {
  const seo = page?.seo;

  if (!seo) return null;

  return {
    description: seo.description ?? null,
    keywords: Array.isArray(seo.keywords) ? seo.keywords.join(', ') : seo.keywords || null,
    ogImage: seo.ogImage?.url || null,
    title: seo.title || null,
  };
}

/**
 * @param {object} [product]
 * @param {string} [currency]
 * @returns {{ availability: string, brand: string, condition: string, currency: string, priceAmount: number|string, retailerItemId: string }|null}
 */
export function resolveProductOgMeta(product, currency = 'AUD') {
  if (!product) return null;

  const price = product.price ?? product.pricePerUnit;
  const orderable = product.inventory?.orderable ?? product.orderable;

  return {
    availability: orderable === false ? 'out of stock' : 'instock',
    brand: product.brand || 'King Living',
    condition: 'new',
    currency,
    priceAmount: price != null ? String(price) : null,
    retailerItemId: product.id,
  };
}

/**
 * @param {{ page?: object, product?: object, category?: object, currency?: string }} args
 * @returns {object}
 */
export function buildContentfulPageSeoProps({page, product, category, currency}) {
  const contentful = resolveContentfulSeoFromPage(page);
  let scapi = null;

  if (page?.productId) {
    scapi = resolveScapiSeoFromProduct(product);
  } else if (page?.categoryId || category) {
    scapi = resolveScapiSeoFromCategory(category);
  } else if (page?.title) {
    scapi = {title: page.title};
  }

  const productMeta = page?.productId ? resolveProductOgMeta(product, currency) : null;

  return {
    contentfulSeo: contentful || undefined,
    description: resolveSeoDescriptionText(scapi?.description) || undefined,
    keywords: scapi?.keywords,
    ogImage: scapi?.ogImage,
    ogType: resolveOgType({page}),
    productMeta: page?.productId ? productMeta : null,
    title: scapi?.title,
  };
}

const SCHEMA_ORG_CONTEXT = 'https://schema.org';
const SCHEMA_ITEM_CONDITION_NEW = `${SCHEMA_ORG_CONTEXT}/NewCondition`;
const SCHEMA_AVAILABILITY_IN_STOCK = `${SCHEMA_ORG_CONTEXT}/InStock`;
const SCHEMA_AVAILABILITY_OUT_OF_STOCK = `${SCHEMA_ORG_CONTEXT}/OutOfStock`;
const SCHEMA_AVAILABILITY_MADE_TO_ORDER = `${SCHEMA_ORG_CONTEXT}/MadeToOrder`;

/**
 * @param {object} [product]
 * @returns {string}
 */
export function mapProductAvailabilityToSchemaOrg(product) {
  if (!product) return SCHEMA_AVAILABILITY_OUT_OF_STOCK;

  const inventory = product.inventory;
  const orderable = inventory?.orderable ?? product.orderable;

  if (orderable === false) {
    return SCHEMA_AVAILABILITY_OUT_OF_STOCK;
  }

  const ats = typeof inventory?.ats === 'number' ? inventory.ats : (
    typeof product.ats === 'number' ? product.ats : null
  );

  if (
    inventory?.perpetual === true ||
    product.perpetual === true ||
    isMtoOrderableProduct(product) ||
    ats === 999999 ||
    (ats != null && ats >= PERPETUAL_ATS_THRESHOLD)
  ) {
    return SCHEMA_AVAILABILITY_MADE_TO_ORDER;
  }

  return SCHEMA_AVAILABILITY_IN_STOCK;
}

/**
 * @param {object} [product]
 * @returns {object|null}
 */
export function resolveProductAggregateRating(product) {
  if (!product) return null;

  const ratingValue = Number(product.c_bvAverageRating);
  const bestRating = Number(product.c_bvRatingRange);
  const reviewCount = Number(product.c_bvReviewCount);

  if (![ratingValue, bestRating, reviewCount].every(Number.isFinite)) {
    return null;
  }

  return {
    '@type': 'AggregateRating',
    bestRating,
    ratingValue,
    'reviewCount': Math.trunc(reviewCount),
  };
}

/**
 * @param {object[]} nodes
 * @returns {{ '@context': string, '@graph': object[] }|null}
 */
export function buildJsonLdGraph(nodes) {
  const graph = (nodes || []).filter(Boolean);

  if (!graph.length) return null;

  return {
    '@context': SCHEMA_ORG_CONTEXT,
    '@graph': graph,
  };
}

/**
 * @param {{ '@graph'?: object[] }|null} baseGraph
 * @param {object|object[]} extraNodes
 * @returns {{ '@context': string, '@graph': object[] }|null}
 */
export function mergeJsonLdGraphs(baseGraph, extraNodes) {
  const base = [...(baseGraph?.['@graph'] || [])];
  const extras = Array.isArray(extraNodes) ? extraNodes : [extraNodes];
  const merged = [...base];

  for (const node of extras.filter(Boolean)) {
    if (node['@type'] === 'FAQPage') {
      const existing = merged.find((item) => item['@type'] === 'FAQPage');

      if (existing) {
        existing.mainEntity = [
          ...(existing.mainEntity || []),
          ...(node.mainEntity || [])
        ];
        continue;
      }
    }

    merged.push(node);
  }

  return buildJsonLdGraph(merged);
}

/** Shared social profiles across markets (production schema, 2026-06-29). */
const ORG_SAME_AS_SHARED = [
  'https://www.instagram.com/kingliving/',
  'https://www.youtube.com/channel/UCwo6CxIRTqHQez1zMF1Qtbg',
  'https://www.linkedin.com/company/king-furniture-australia-pty-ltd/',
  'https://au.pinterest.com/kingliving/'
];

/**
 * Per-market Facebook URL from production Organization schema.
 * @param {string} facebookUrl
 * @returns {string[]}
 */
function buildOrgSameAs(facebookUrl) {
  return [facebookUrl, ...ORG_SAME_AS_SHARED];
}

/**
 * TSD Organization schema logo — fixed path + dimensions per market.
 * Site pref `brandLogo` is not used: SCAPI/BM does not expose image width/height
 * required by Schema.org ImageObject.
 */
const ORG_SCHEMA_LOGO_SIZE = 112;
const ORG_SCHEMA_LOGO_PATH = '/media/wysiwyg/logo-schema.png';

/**
 * @param {string} origin
 * @returns {{ url: string, width: number, height: number }}
 */
function buildOrgSchemaLogo(origin) {
  return {
    height: ORG_SCHEMA_LOGO_SIZE,
    url: `${origin.replace(/\/+$/, '')}${ORG_SCHEMA_LOGO_PATH}`,
    width: ORG_SCHEMA_LOGO_SIZE,
  };
}

function buildOrgSiteConfig(origin, facebookUrl) {
  return {
    logo: buildOrgSchemaLogo(origin),
    name: 'King Living',
    sameAs: buildOrgSameAs(facebookUrl),
  };
}

/** Organization JSON-LD content per `kl-*` site (logo + sameAs hardcoded per TSD). */
const SEO_ORGANIZATION_BY_SITE_ID = {
  'kl-au': buildOrgSiteConfig(
    'https://www.kingliving.com.au',
    'https://www.facebook.com/people/King-Living-US/61556162704250'
  ),
  'kl-nz': buildOrgSiteConfig('https://www.kingliving.co.nz', 'https://www.facebook.com/KingLiving.NZ/'),
  'kl-uk': buildOrgSiteConfig('https://www.kingliving.co.uk', 'https://www.facebook.com/KingLiving.UK/'),
  'kl-us': buildOrgSiteConfig(
    'https://www.kingliving.com',
    'https://www.facebook.com/people/King-Living-US/61556162704250'
  ),
  'kl-ca': buildOrgSiteConfig('https://www.kingliving.ca', 'https://www.facebook.com/KingLiving.CA/'),
  'kl-sg': buildOrgSiteConfig('https://www.kingliving.com.sg', 'https://www.facebook.com/KingLiving.SG/'),
  'kl-my': buildOrgSiteConfig('https://www.kingliving.com.my', 'https://www.facebook.com/KingLiving.MY/'),
};

/**
 * @param {string} [siteId]
 * @returns {(typeof SEO_ORGANIZATION_BY_SITE_ID)['kl-au']|null}
 */
function getSeoOrganizationForSite(siteId) {
  if (!siteId) return SEO_ORGANIZATION_BY_SITE_ID['kl-au'] ?? null;

  return SEO_ORGANIZATION_BY_SITE_ID[siteId] ?? SEO_ORGANIZATION_BY_SITE_ID['kl-au'] ?? null;
}

/**
 * @param {string} [siteOriginUrl]
 * @param {string} fragment
 * @returns {string|null}
 */
function buildSchemaFragmentId(siteOriginUrl, fragment) {
  if (!siteOriginUrl || !fragment) return null;

  const base = siteOriginUrl.split('#')[0];
  const normalized = base.endsWith('/') ? base : `${base}/`;

  return `${normalized}#${fragment}`;
}

/**
 * @param {object} args
 * @returns {object|null}
 */
export function buildOrganizationJsonLd({siteId, url, siteOriginUrl}) {
  const config = getSeoOrganizationForSite(siteId);

  if (!config) return null;

  const node = {
    '@type': 'Organization',
    'name': config.name,
    'sameAs': config.sameAs,
  };

  const schemaOriginUrl = siteOriginUrl || url;
  const organizationId = buildSchemaFragmentId(schemaOriginUrl, 'organization');

  if (organizationId) {
    node['@id'] = organizationId;
  }

  if (url) {
    node.url = url;
  }

  if (config.logo?.url) {
    node.logo = {
      '@type': 'ImageObject',
      'height': config.logo.height,
      'url': config.logo.url,
      'width': config.logo.width,
    };
  }

  return node;
}

/**
 * @param {{ name?: string, url?: string|null, description?: string|null, siteOriginUrl?: string|null }} args
 * @returns {object|null}
 */
export function buildWebSiteJsonLd({name, url, description, siteOriginUrl}) {
  if (!name && !url) return null;

  const node = {
    '@type': 'WebSite',
    'name': name || DEFAULT_SITE_TITLE,
  };

  const websiteId = buildSchemaFragmentId(siteOriginUrl || url, 'website');

  if (websiteId) {
    node['@id'] = websiteId;
  }

  if (url) node.url = url;
  if (description) node.description = description;

  return node;
}

/**
 * TSD WebSite + Organization @graph — Homepage, PLP, Stores, Search.
 * @param {{ siteId?: string, pageUrl?: string|null, siteOriginUrl?: string|null, description?: string|null }} args
 * @returns {object|null}
 */
export function buildOrgWebSiteJsonLdGraph({siteId, pageUrl, siteOriginUrl, description}) {
  if (!pageUrl) return null;

  const org = buildOrganizationJsonLd({
    siteId,
    siteOriginUrl,
    url: siteOriginUrl || pageUrl,
  });
  const web = buildWebSiteJsonLd({
    description,
    name: DEFAULT_SITE_TITLE,
    siteOriginUrl,
    url: pageUrl,
  });

  return buildJsonLdGraph([org, web].filter(Boolean));
}

/**
 * @param {{ product?: object, name?: string, description?: string|null, url?: string|null, currency?: string }} args
 * @returns {object|null}
 */
export function buildProductJsonLd({product, name, description, url, currency = 'AUD'}) {
  if (!product) return null;

  const ogMeta = resolveProductOgMeta(product, currency);
  const imageUrl = resolveProductImageUrl(product);
  const aggregateRating = resolveProductAggregateRating(product);

  const node = {
    '@type': 'Product',
    'name': name || product.name || product.id,
    'sku': product.id,
  };

  if (description) node.description = description;
  if (imageUrl) node.image = imageUrl;
  if (url) node.url = url;

  if (ogMeta?.brand) {
    node.brand = {
      '@type': 'Brand',
      'name': ogMeta.brand,
    };
  }

  if (ogMeta?.priceAmount != null && url) {
    node.offers = {
      '@type': 'Offer',
      'availability': mapProductAvailabilityToSchemaOrg(product),
      'itemCondition': SCHEMA_ITEM_CONDITION_NEW,
      'price': ogMeta.priceAmount,
      'priceCurrency': ogMeta.currency || currency,
      url,
    };
  }

  if (aggregateRating) {
    node.aggregateRating = aggregateRating;
  }

  return node;
}

/**
 * @param {object[]} [accordions]
 * @returns {{ question: string, answer: string }[]}
 */
export function mapAccordionsToFaqQuestions(accordions) {
  return (accordions || [])
    .map((accordion) => {
      const question = accordion?.title?.trim();
      const answer = richTextToPlainText(accordion?.body?.json);

      if (!question || !answer) return null;

      return {answer, question};
    })
    .filter(Boolean);
}

/**
 * @param {{ questions?: { question: string, answer: string }[] }} args
 * @returns {object|null}
 */
export function buildFaqPageJsonLd({questions}) {
  const mainEntity = (questions || [])
    .filter((item) => item?.question && item?.answer)
    .map(({question, answer}) => ({
      '@type': 'Question',
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': answer,
      },
      'name': question,
    }));

  if (!mainEntity.length) return null;

  return {
    '@type': 'FAQPage',
    mainEntity,
  };
}

/**
 * @param {{ title?: string, description?: string|null, author?: string, datePublished?: string, imageUrl?: string|null, url?: string|null }} args
 * @returns {object|null}
 */
function buildBlogPostingJsonLd({
  title,
  description,
  author,
  datePublished,
  imageUrl,
  url,
}) {
  if (!title || !url) return null;

  const node = {
    '@type': 'BlogPosting',
    'headline': title,
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': url,
    },
    url,
  };

  if (description) node.description = description;
  if (imageUrl) node.image = imageUrl;
  if (datePublished) node.datePublished = datePublished;

  if (author) {
    node.author = {
      '@type': 'Person',
      'name': String(author),
    };
  }

  return node;
}

export function buildPageJsonLdGraph({
  page,
  product,
  category,
  blog,
  appOrigin,
  pathname,
  currency,
  siteId,
  noIndex,
  faqQuestions,
}) {
  if (!shouldEmitJsonLd({noIndex, pathname})) return null;

  const pageUrl = shouldEmitSeoLinks({noIndex, pathname}) ?
    resolveCanonicalUrl({appOrigin, noIndex, pathname}) :
    resolveJsonLdPageUrl({appOrigin, pathname});

  if (!pageUrl) return null;

  const siteOriginUrl = resolveJsonLdPageUrl({appOrigin, pathname: '/'});
  const seoProps = page ?
    buildContentfulPageSeoProps({category, currency, page, product}) :
    category ?
      {
        description: category.pageDescription,
        title: category.pageTitle || category.name,
      } :
      {};

  if (page?.productId) {
    if (!product) return null;

    const productNode = buildProductJsonLd({
      currency,
      description: resolveSeoDescriptionText(seoProps.description),
      name: seoProps.title || product.name,
      product,
      url: pageUrl,
    });

    return productNode ? buildJsonLdGraph([productNode]) : null;
  }

  if (isBlogArticlePage(page)) {
    const pageSeoOgImage = page?.seo?.ogImage?.url || null;
    const blogTileImage = blog?.blogTileImage;
    const schemaDescription = blog ?
      (
        richTextFirstBlockPlainText(blog.blogContent?.json) ||
        resolveSeoDescriptionText(blog.description) ||
        resolveSeoDescriptionText(page?.seo?.description) ||
        null
      ) :
      null;
    const imageUrl = pageSeoOgImage || (blogTileImage ?
      (
        blogTileImage.desktopAsset?.url ||
        blogTileImage.tabletAsset?.url ||
        blogTileImage.mobileAsset?.url ||
        blogTileImage.url ||
        null
      ) :
      null);

    const blogNode = buildBlogPostingJsonLd({
      author: blog?.author,
      datePublished: blog?.datePublished || undefined,
      description: schemaDescription || undefined,
      imageUrl,
      title: blog?.title || seoProps.title,
      url: pageUrl,
    });

    return blogNode ? buildJsonLdGraph([blogNode]) : null;
  }

  if (isFaqCmsPage(page)) {
    return buildJsonLdGraph([buildFaqPageJsonLd({questions: faqQuestions})]);
  }

  return mergeJsonLdGraphs(
    buildOrgWebSiteJsonLdGraph({
      pageUrl,
      siteId,
      siteOriginUrl,
    }),
    faqQuestions?.length ? buildFaqPageJsonLd({questions: faqQuestions}) : null
  );
}

/**
 * @param {{ page?: object, product?: object, category?: object, currency?: string, appOrigin?: string, pathname?: string, siteId?: string, noIndex?: boolean }} args
 * @returns {object}
 */
export function buildPageSeoBundle({
  page,
  product,
  category,
  blog,
  currency,
  appOrigin,
  pathname,
  siteId,
  noIndex,
  faqQuestions,
}) {
  return {
    ...buildContentfulPageSeoProps({category, currency, page, product}),
    jsonLdGraph: buildPageJsonLdGraph({
      appOrigin,
      blog,
      category,
      currency,
      faqQuestions,
      noIndex,
      page,
      pathname,
      product,
      siteId,
    }),
  };
}

export const SEO_MANAGED_META_NAMES = new Set(['description', 'keywords', 'robots', 'title']);
